import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CalendarCheck2, CheckCircle2, Loader2 } from "lucide-react";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const phoneOk = (v) => v.replace(/[^0-9]/g, "").length >= 7;

export default function ScheduleDialog({ quote, metalLabel }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setForm({ name: "", email: "", phone: "" });
    setErrors({});
    setSuccess(false);
    setSubmitting(false);
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      setTimeout(reset, 200);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim() || !emailOk(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim() || !phoneOk(form.phone)) e.phone = "Enter a valid phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      await api.post("/schedule", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        quote_total: quote?.total ?? null,
        metal: quote?.metal ?? null,
        metal_label: metalLabel ?? null,
        line_items: quote?.line_items ?? [],
        labor_total: quote?.labor_total ?? 0,
        materials_total: quote?.materials_total ?? 0,
        shop_fee: quote?.shop_fee ?? 0,
      });
      setSuccess(true);
    } catch (err) {
      setErrors({
        _form: err?.response?.data?.detail || "Could not submit. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          className="rounded-none bg-charcoal-900 text-white hover:bg-charcoal-800 uppercase tracking-widest text-xs px-6 py-5 w-full"
          data-testid="schedule-repair-btn"
        >
          <CalendarCheck2 size={16} strokeWidth={1.6} className="mr-2" />
          Schedule Repair
        </Button>
      </DialogTrigger>

      <DialogContent
        className="rounded-none border-zinc-200 bg-white p-0 max-w-md sm:max-w-md gap-0"
        data-testid="schedule-dialog"
      >
        {success ? (
          <SuccessState
            onClose={() => onOpenChange(false)}
            quote={quote}
            metalLabel={metalLabel}
            form={form}
          />
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-2 text-left space-y-2">
              <div className="overline">Book an appointment</div>
              <DialogTitle className="font-serif text-3xl font-light leading-tight text-charcoal-900">
                Schedule Repair
              </DialogTitle>
              <DialogDescription className="text-sm text-charcoal-500">
                Share your details and our bench team will reach out to confirm a time.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="px-8 pb-8 pt-6 space-y-5" noValidate>
              {quote ? (
                <div
                  className="bg-champagne-50 border border-champagne-200 px-4 py-3 flex items-center justify-between"
                  data-testid="dialog-quote-summary"
                >
                  <div className="text-xs uppercase tracking-widest text-champagne-700">
                    Estimated quote
                  </div>
                  <div className="font-serif text-lg text-charcoal-900">
                    ${Number(quote.total).toFixed(2)}
                  </div>
                </div>
              ) : null}

              <Field label="Full name" error={errors.name}>
                <Input
                  className="lux-input"
                  data-testid="schedule-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Goldsmith"
                  autoComplete="name"
                />
              </Field>
              <Field label="Email" error={errors.email}>
                <Input
                  type="email"
                  className="lux-input"
                  data-testid="schedule-input-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com"
                  autoComplete="email"
                />
              </Field>
              <Field label="Phone number" error={errors.phone}>
                <Input
                  type="tel"
                  className="lux-input"
                  data-testid="schedule-input-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  autoComplete="tel"
                />
              </Field>

              {errors._form ? (
                <div
                  className="text-sm text-red-600"
                  data-testid="schedule-form-error"
                >
                  {errors._form}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={submitting}
                className="rounded-none bg-charcoal-900 text-white hover:bg-charcoal-800 uppercase tracking-widest text-xs px-6 py-5 w-full disabled:opacity-60"
                data-testid="schedule-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={14} />
                    Sending…
                  </>
                ) : (
                  "Request Appointment"
                )}
              </Button>

              <p className="text-[11px] text-charcoal-400 leading-relaxed">
                By submitting you agree to be contacted about your repair. We will
                never share your details.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="lux-label">{label}</span>
      {children}
      {error ? (
        <span
          className="block mt-1 text-xs text-red-600"
          data-testid={`error-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

function SuccessState({ onClose, quote, metalLabel, form }) {
  return (
    <div
      className="px-8 py-12 text-center space-y-5"
      data-testid="schedule-success"
    >
      <div className="flex justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 border border-champagne-200">
          <CheckCircle2 className="text-champagne-600" size={28} strokeWidth={1.4} />
        </div>
      </div>
      <div className="overline">Request received</div>
      <h3
        className="font-serif text-2xl font-light leading-tight text-charcoal-900"
        data-testid="schedule-success-title"
      >
        Thank you{form?.name ? `, ${form.name.split(" ")[0]}` : ""}.
      </h3>
      <p
        className="text-base text-charcoal-500 leading-relaxed max-w-xs mx-auto"
        data-testid="schedule-success-message"
      >
        We will contact you shortly to schedule your jewelry repair appointment.
      </p>
      {quote ? (
        <div className="border-t border-dashed border-zinc-200 pt-4 text-xs text-charcoal-400">
          On file: {metalLabel || quote.metal} · estimate ${Number(quote.total).toFixed(2)}
        </div>
      ) : null}
      <Button
        type="button"
        onClick={onClose}
        className="rounded-none bg-charcoal-900 text-white hover:bg-charcoal-800 uppercase tracking-widest text-xs px-6 py-5"
        data-testid="schedule-success-close"
      >
        Close
      </Button>
    </div>
  );
}
